import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { Check, Ticket, ShieldCheck, ArrowRight, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import './Checkout.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://talk-to-krishna-japan.onrender.com';

function Checkout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, login } = useAuth();

    // Fallback if accessed directly without state
    const selectedPlan = location.state?.plan || {
        name: 'プラン未選択',
        price: '¥0',
        period: '',
        description: ''
    };

    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [checkoutComplete, setCheckoutComplete] = useState(false);
    const [isMandateStep, setIsMandateStep] = useState(false);
    const [activeCoupons, setActiveCoupons] = useState([]);

    const basePriceNum = parseInt(selectedPlan.price.replace(/[¥,]/g, '')) || 0;
    const [finalPrice, setFinalPrice] = useState(basePriceNum);

    const fetchActiveCoupons = useCallback(async () => {
        try {
            const res = await axios.get(API_ENDPOINTS.ACTIVE_COUPONS, {
                params: { plan_id: selectedPlan.plan_id }
            });
            if (res.data.success) {
                setActiveCoupons(res.data.coupons);
            }
        } catch (err) {
            console.error("Failed to fetch coupons:", err);
        }
    }, [selectedPlan.plan_id]);

    const handleApplyCoupon = useCallback(async () => {
        if (!couponCode.trim()) return;

        setIsLoading(true);
        setCouponError('');

        try {
            const response = await axios.post(API_ENDPOINTS.VALIDATE_COUPON, {
                code: couponCode
            });

            if (response.data.success) {
                const coupon = response.data.coupon;
                setAppliedCoupon(coupon);
                setCouponCode('');

                let discountAmount = 0;
                if (coupon.discount_type === 'percentage') {
                    discountAmount = Math.round((basePriceNum * coupon.discount_value) / 100);
                } else if (coupon.discount_type === 'fixed_value') {
                    discountAmount = Math.round(coupon.discount_value);
                } else if (coupon.discount_type === 'free_access') {
                    discountAmount = basePriceNum;
                }

                setFinalPrice(Math.max(0, basePriceNum - discountAmount));
            }
        } catch (err) {
            setCouponError(err.response?.data?.error || '無効なクーポンコードです');
            setAppliedCoupon(null);
            setFinalPrice(basePriceNum);
        } finally {
            setIsLoading(false);
        }
    }, [couponCode, basePriceNum]);

    const handleAutoApply = useCallback(async (code) => {
        setCouponCode(code);
        setIsLoading(true);
        setCouponError('');

        try {
            const response = await axios.post(API_ENDPOINTS.VALIDATE_COUPON, {
                code: code
            });

            if (response.data.success) {
                const coupon = response.data.coupon;
                setAppliedCoupon(coupon);
                setCouponCode('');

                let discountAmount = 0;
                if (coupon.discount_type === 'percentage') {
                    discountAmount = Math.round((basePriceNum * coupon.discount_value) / 100);
                } else if (coupon.discount_type === 'fixed_value') {
                    discountAmount = Math.round(coupon.discount_value);
                } else if (coupon.discount_type === 'free_access') {
                    discountAmount = basePriceNum;
                }

                setFinalPrice(Math.max(0, basePriceNum - discountAmount));
            }
        } catch (err) {
            setCouponError(err.response?.data?.error || '無効なクーポンコードです');
            setAppliedCoupon(null);
            setFinalPrice(basePriceNum);
        } finally {
            setIsLoading(false);
        }
    }, [basePriceNum]);

    useEffect(() => {
        if (!location.state?.plan) {
            navigate('/pricing');
            return;
        }
        fetchActiveCoupons();
        
        // Auto-apply coupon if passed in state
        if (location.state?.coupon) {
            handleAutoApply(location.state.coupon);
        }
    }, [location.state, navigate, fetchActiveCoupons, handleAutoApply]);

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setFinalPrice(basePriceNum);
    };

    const setupSubscription = async (userId, planId) => {
        try {
            setIsLoading(true);
            const subRes = await axios.post(`${API_BASE_URL}/api/create-razorpay-subscription`, {
                user_id: userId,
                plan_id: planId
            });

            if (!subRes.data.success) throw new Error(subRes.data.error || "サブスクリプションの作成に失敗しました");

            const { subscription_id, key_id } = subRes.data;

            const options = {
                key: key_id,
                subscription_id: subscription_id,
                name: "TTK Japan",
                description: "月額プランの自動更新を有効にする",
                image: "/logo.png",
                handler: function (response) {
                    console.log("Subscription mandate successful");
                    const updatedUser = { ...user, has_chat_access: true };
                    login(updatedUser);
                    setCheckoutComplete(true);
                    setTimeout(() => {
                        navigate('/chat');
                    }, 3000);
                },
                prefill: {
                    name: user.name,
                    email: user.email,
                },
                theme: {
                    color: "#4f46e5"
                },
                modal: {
                    ondismiss: function() {
                        // If they close the mandate, they already paid once, 
                        // so we can still let them in but notify them
                        alert("自動更新の設定が完了していません。プロフィールページからいつでも設定できます。");
                        const updatedUser = { ...user, has_chat_access: true };
                        login(updatedUser);
                        setCheckoutComplete(true);
                        setTimeout(() => navigate('/chat'), 3000);
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (err) {
            console.error('Subscription setup error:', err);
            // Don't block access if main payment succeeded
            alert("自動更新の設定に問題が発生しました。まず1ヶ月分のアクセスを付与します。");
            const updatedUser = { ...user, has_chat_access: true };
            login(updatedUser);
            setCheckoutComplete(true);
            setTimeout(() => navigate('/chat'), 3000);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCompletePurchase = async () => {
        if (!user) {
            navigate('/login', { state: { from: location } });
            return;
        }

        setIsLoading(true);

        try {
            // CASE: 100% Discount (FREE)
            if (finalPrice === 0 && appliedCoupon) {
                const res = await axios.post(`${API_BASE_URL}/api/grant-free-access`, {
                    user_id: user.id,
                    plan_id: selectedPlan.plan_id || 'monthly_30',
                    coupon_code: appliedCoupon.code
                });

                if (res.data.success) {
                    const updatedUser = { ...user, has_chat_access: true };
                    login(updatedUser);
                    setCheckoutComplete(true);
                    setTimeout(() => {
                        navigate('/chat');
                    }, 3000);
                    return;
                } else {
                    throw new Error(res.data.error || 'アクセスの付与に失敗しました');
                }
            }

            // Step 1: Create Razorpay Order on the backend
            const orderRes = await axios.post(`${API_BASE_URL}/api/create-razorpay-order`, {
                user_id: user.id,
                plan_id: selectedPlan.plan_id || 'monthly_30',
                amount: finalPrice,
                email: user.email,
                coupon_code: appliedCoupon?.code,
                discount_amount: basePriceNum - finalPrice
            });

            if (!orderRes.data.success) {
                throw new Error(orderRes.data.error || '注文の作成に失敗しました');
            }

            const { order_id, amount, currency, key_id } = orderRes.data;

            // Step 2: Initialize Razorpay Checkout
            const options = {
                key: key_id,
                amount: amount,
                currency: currency,
                name: "TTK Japan",
                description: `${selectedPlan.name}の初回分支払い`,
                image: "/logo.png",
                order_id: order_id,
                handler: async function (response) {
                    // Step 3: Verify payment on the backend
                    try {
                        setIsLoading(true);
                        const verifyRes = await axios.post(`${API_BASE_URL}/api/verify-payment`, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            user_id: user.id
                        });

                        if (verifyRes.data.success) {
                            // If this was a monthly plan, trigger subscription mandate
                            if (selectedPlan.plan_id === 'monthly_30') {
                                setIsMandateStep(true);
                                setupSubscription(user.id, selectedPlan.plan_id);
                            } else {
                                // For yearly plans, just complete
                                const updatedUser = { ...user, has_chat_access: true };
                                login(updatedUser);
                                setCheckoutComplete(true);
                                setTimeout(() => {
                                    navigate('/chat');
                                }, 3000);
                            }
                        } else {
                            alert('決済の確認に失敗しました。サポートにお問い合わせください。');
                            setIsLoading(false);
                        }
                    } catch (err) {
                        console.error('Verification error:', err);
                        alert('決済確認中にエラーが発生しました。');
                        setIsLoading(false);
                    }
                },
                prefill: {
                    name: user.name,
                    email: user.email,
                },
                theme: {
                    color: "#4f46e5"
                },
                modal: {
                    ondismiss: function() {
                        setIsLoading(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', function (response) {
                alert('決済に失敗しました: ' + response.error.description);
                setIsLoading(false);
            });
            rzp.open();

        } catch (err) {
            console.error('Checkout error:', err);
            alert(err.message || 'チェックアウト中にエラーが発生しました。もう一度お試しください。');
            setIsLoading(false);
        }
    };

    if (checkoutComplete) {
        return (
            <div className="checkout-page">
                <Navbar />
                <div className="checkout-success-container container">
                    <div className="success-content">
                        <div className="success-icon-wrapper">
                            <Check size={48} />
                        </div>
                        <h1>注文が完了しました！</h1>
                        <p>プランの有効化に成功しました。まもなくチャットページに移動します...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout-page">
            <Navbar />

            <div className="checkout-container container">
                <div className="checkout-layout">
                    {/* Left: Plan Summary */}
                    <div className="checkout-summary">
                        <div className="checkout-card">
                            <h2 className="section-title">ご注文内容</h2>
                            <div className="plan-item">
                                <div className="plan-info">
                                    <h3>{selectedPlan.name}プラン</h3>
                                    <p>{selectedPlan.description}</p>
                                </div>
                                <div className="plan-price-side">
                                    {selectedPlan.price}
                                </div>
                            </div>

                            <hr className="divider" />

                            <div className="price-details">
                                {selectedPlan.plan_id === 'monthly_30' && (
                                    <div className="subscription-info-badge">
                                        初回 ¥{finalPrice.toLocaleString()}、次回から毎月 ¥14,999
                                    </div>
                                )}
                                <div className="price-row">
                                    <span>小計</span>
                                    <span>¥{basePriceNum.toLocaleString()}</span>
                                </div>

                                {appliedCoupon && (
                                    <div className="price-row discount">
                                        <span>クーポン適用 ({appliedCoupon.code})</span>
                                        <span>- ¥{(basePriceNum - finalPrice).toLocaleString()}</span>
                                    </div>
                                )}

                                <div className="price-row total">
                                    <span>合計</span>
                                    <span className="final-total">¥{finalPrice.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Payment & Options */}
                    <div className="checkout-actions">
                        {/* Coupon Form */}
                        <div className="checkout-card coupon-section">
                            <h3>クーポンを利用する</h3>
                            {!appliedCoupon ? (
                                <div className="coupon-input-group">
                                    <input
                                        type="text"
                                        placeholder="クーポンコードを入力"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                        className="coupon-input"
                                    />
                                    <button
                                        onClick={handleApplyCoupon}
                                        disabled={isLoading || !couponCode}
                                        className="apply-btn"
                                    >
                                        適用
                                    </button>
                                </div>
                            ) : (
                                <div className="applied-badge">
                                    <Ticket size={18} />
                                    <span>{appliedCoupon.code} が適用されました</span>
                                    <button onClick={removeCoupon} className="remove-btn">
                                        <X size={14} />
                                    </button>
                                </div>
                            )}
                            {couponError && <p className="coupon-error">{couponError}</p>}

                            {!appliedCoupon && activeCoupons.length > 0 && (
                                <div className="available-coupons">
                                    <p className="available-title">利用可能なクーポン:</p>
                                    <div className="coupon-chips">
                                        {activeCoupons.map((coupon) => (
                                            <button
                                                key={coupon.code}
                                                className="coupon-chip"
                                                onClick={() => handleAutoApply(coupon.code)}
                                                disabled={isLoading}
                                            >
                                                <Ticket size={14} />
                                                <span>{coupon.code}</span>
                                                <span className="chip-value">
                                                    {coupon.discount_type === 'free_access' ? '無料' :
                                                     coupon.discount_type === 'percentage' ? `${coupon.discount_value}% OFF` :
                                                     `¥${coupon.discount_value.toLocaleString()} OFF`}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Payment Button */}
                        <div className="checkout-card payment-section">
                            <div className="security-tag">
                                <ShieldCheck size={16} />
                                <span>安全なSSL暗号化通信</span>
                            </div>

                            <button
                                className="complete-btn"
                                onClick={handleCompletePurchase}
                                disabled={isLoading}
                            >
                                {isLoading ? (isMandateStep ? '自動更新を設定中...' : '処理中...') : '決済を進行する'}
                                {!isLoading && <ArrowRight size={20} />}
                            </button>

                            <p className="payment-note">
                                ボタンを押すことで、当社の利用規約に同意したものとみなされます。
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Checkout;
